import {
	getLibelleImputation,
	getSymbol,
	TreeSymbol,
} from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import TypeRegroupement from '../constants/feuilleSaisie/typeRegroupement';
import type TreeRow from '../interfaces/feuilleSaisie/treeRow';

export interface CreateImputation {
	codeChapitre: string;
	codeArticle: string;
	codeFonction: string;
	codeOperation: string;
	codeVentilation: string;
	codeAnalytique: string;
	sens: string;
	section: string;
	cha_libelle?: string;
	art_libelle?: string;
	fon_libelle?: string;
	ope_libelle?: string;
	ven_libelle?: string;
	ana_libelle?: string;
}

export const checkImputation = (imputation: CreateImputation) => {
	if (isNil(imputation?.sens)) {
		throw new Error('Le sens est obligatoire');
	}

	if (isNil(imputation?.section)) {
		throw new Error('La section est obligatoire');
	}

	if (isNil(imputation?.codeChapitre)) {
		throw new Error('La chapitre est obligatoire');
	}

	if (isNil(imputation?.codeArticle)) {
		throw new Error("L'article est obligatoire");
	}

	if (imputation?.section === 'I' && isNil(imputation?.codeOperation)) {
		throw new Error("L'opération est obligatoire en investissement");
	}
};

const getRegroupementLibelle = (rowData: TreeRow): string[] => {
	const result = [];
	rowData?.typesRegroupement?.forEach((typeRegroupement) => {
		if (typeRegroupement.code !== TypeRegroupement.IMPUTATION) {
			result.push(rowData?.[typeRegroupement.codeColumnName]);
		}
	});

	return result;
};

export const getImputationText = (rowData: TreeRow): string => {
	let parent: TreeRow = getSymbol(rowData, TreeSymbol.PARENT);
	// Si nous avons plusieurs elements de l'imputation.
	if (parent) {
		let codes = getRegroupementLibelle(rowData);
		// On remonte à chaque parent, et on récupère son code
		while (parent) {
			codes = getRegroupementLibelle(parent).concat(codes);
			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		return codes.join(' | ');
	}
	return getRegroupementLibelle(rowData).join(' | ');
};

/**
 * Libelle des codes d'une imputation au format habituel de l'appli
 * (les axes absents ou masques ne sont pas affiches)
 */
export const getLibelleCodesImputation = (
	sens: string,
	section: string,
	chapitre: string,
	article: string,
	operation?: string | null,
	fonction?: string | null,
	ventilation?: string | null,
	analytique?: string | null,
): string =>
	getLibelleImputation(
		sens,
		section,
		chapitre,
		article,
		operation,
		fonction,
		ventilation,
		analytique,
	)
		// Un axe absent laisse un double espace
		.replace(/\s+/g, ' ')
		.trim();

// Corps identifiant une ligne budgetaire d'une simulation, commun a la
// creation d'un montant et a l'import d'idees Vision 360
export interface ImputationLigne {
	identifiantAnalytique: number | null;
	identifiantArticle?: number;
	identifiantChapitre?: number;
	identifiantFonction?: number;
	identifiantOperation?: number;
	identifiantSimulation: number;
	identifiantVentilation: number | null;
	sens?: string;
}

export const getImputationLigne = (
	identifiantSimulation: number,
	rowData?: TreeRow,
): ImputationLigne => ({
	identifiantAnalytique: rowData?.idcm_analytique ?? null,
	identifiantArticle: rowData?.idcm_article,
	identifiantChapitre: rowData?.idcm_chapitre,
	identifiantFonction: rowData?.idcm_fonction,
	identifiantOperation: rowData?.idcm_operation,
	identifiantSimulation,
	identifiantVentilation: rowData?.idcm_ventilation ?? null,
	sens: rowData?.imp_sens,
});

// Parametres identifiant une ligne budgetaire d'une simulation, communs aux
// routes qui travaillent "par imputation" (montants, idees Vision 360...)
export const getImputationParams = (
	identifiantSimulation: number,
	rowData: TreeRow,
): Record<string, string> => ({
	identifiantSimulation: identifiantSimulation.toString(),
	...(!isNil(rowData.idcm_analytique)
		? { identifiantAnalytique: rowData.idcm_analytique.toString() }
		: {}),
	...(!isNil(rowData.idcm_ventilation)
		? { identifiantVentilation: rowData.idcm_ventilation.toString() }
		: {}),
	...(!isNil(rowData.idcm_fonction)
		? { identifiantFonction: rowData.idcm_fonction.toString() }
		: {}),
	...(!isNil(rowData.idcm_operation)
		? { identifiantOperation: rowData.idcm_operation.toString() }
		: {}),
	identifiantChapitre: rowData.idcm_chapitre?.toString(),
	identifiantArticle: rowData.idcm_article?.toString(),
	sens: rowData.imp_sens,
});
